import type { Point, RangeRef } from 'slate';

import type { SpellcheckLint } from './spellcheck-vars';
import { SPELLCHECK_STATUS } from './spellcheck-vars';
import { createOwnedTabScopedRegistry } from './tab-scoped-registry';

export const EMPTY_SPELLCHECK_RETIRED_FROM: ReadonlyMap<string, Point | null> = new Map();

export interface SpellcheckState {
  accepted: readonly SpellcheckLint[];
  checking: boolean;
  ignoreRequests: readonly SpellcheckLint[];
  lints: SpellcheckLint[];
  ranges: RangeRef[];
  /**
   * Paint cutoff keyed by paragraph path. Range refs stay published; a paragraph hides redlines
   * from this point onward until the next pass.
   */
  retiredFromByParagraph: ReadonlyMap<string, Point | null>;
  status: string;
  typing: boolean;
}

const initialSpellcheckState: SpellcheckState = {
  accepted: [],
  checking: false,
  ignoreRequests: [],
  lints: [],
  ranges: [],
  retiredFromByParagraph: EMPTY_SPELLCHECK_RETIRED_FROM,
  status: SPELLCHECK_STATUS.off,
  typing: false,
};

/**
 * Spellcheck state belongs to one tab. Each tab holds its own document and its own Harper worker,
 * so the problems found in one must not appear in another.
 *
 * No `areEqual`: the lint and range-ref arrays are rebuilt by every pass, so a deep comparison
 * would walk every range on a hot path. The call sites that can skip a write compare identity
 * themselves.
 */
const spellcheckStateRegistry = createOwnedTabScopedRegistry({
  initialState: initialSpellcheckState,
  useTabIdErrorMessage: 'Spellcheck state must be used within a spellcheck state scope.',
});

export const SpellcheckStateProvider = spellcheckStateRegistry.Provider;
export const SpellcheckStateScope = spellcheckStateRegistry.Scope;

export function useSpellcheckStateSelector<TValue>(
  selector: (state: SpellcheckState) => TValue,
  isEqual?: (left: TValue, right: TValue) => boolean
): TValue {
  return spellcheckStateRegistry.useSelector(selector, isEqual);
}

export function useSpellcheckTabId(): UUID {
  return spellcheckStateRegistry.useTabId();
}

export function getSpellcheckStateForTabId(tabId: UUID | null): SpellcheckState | null {
  return spellcheckStateRegistry.getForTabId(tabId);
}

export function updateSpellcheckStateForTabId(
  tabId: UUID,
  updater: (state: SpellcheckState) => SpellcheckState
): void {
  spellcheckStateRegistry.updateForTabId(tabId, updater);
}

/** Drops everything on screen for one tab: the drawer's problem list, the redlines, the busy flags. */
export function clearSpellcheckStateForTabId(tabId: UUID): void {
  updateSpellcheckStateForTabId(tabId, state => ({
    ...state,
    accepted: [],
    checking: false,
    lints: [],
    ranges: [],
    retiredFromByParagraph: EMPTY_SPELLCHECK_RETIRED_FROM,
    typing: false,
  }));
}
